import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../lib/api';
import { calendarWriteWarning } from '../lib/calendarWarnings';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Google Calendar connection status.
 * (Outlook status is fetched separately via useOutlookCalendarStatus in Calendar.jsx.)
 */
export function useCalendarStatus() {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['calendar', 'status', 'v2'],
        queryFn: async () => {
            try {
                const data = await apiFetch('/oauth/calendar/status', getAuthHeader());
                return {
                    google: { connected: data?.connected || false, email: data?.calendar_email || null },
                    outlook: { status: 'disconnected' },
                };
            } catch {
                return { google: { connected: false }, outlook: { status: 'disconnected' } };
            }
        },
        staleTime: 5 * 60 * 1000,
    });
}

/**
 * No filter applied. The calendar page's "My calendars" rail (#681) is the one
 * caller that passes a real value (buildTagFilters); the day-timeline panel and
 * the event lookup stay unfiltered on purpose — a hidden calendar must not make
 * an event unreachable by its link.
 */
export const NO_FILTERS = Object.freeze({ key: '', params: '' });

/**
 * The events query's filter for the rail's hidden set (#681): a stable KEY (so
 * a changed selection never reads the previous selection's cache) and the URL
 * params the server filters on — in the aggregator, after the merge, so every
 * source and the whole range honour it. Nothing hidden → NO_FILTERS, sharing
 * the unfiltered cache with the rest of the app.
 * @param {{tagIds: string[], untagged: boolean}} hidden
 */
export function buildTagFilters(hidden) {
    const ids = [...new Set(hidden?.tagIds || [])].sort();
    const untagged = Boolean(hidden?.untagged);
    if (!ids.length && !untagged) return NO_FILTERS;
    const params = new URLSearchParams();
    if (ids.length) params.set('hidden_tags', ids.join(','));
    if (untagged) params.set('hide_untagged', 'true');
    return Object.freeze({ key: `hidden:${untagged ? 1 : 0}:${ids.join(',')}`, params: params.toString() });
}

/**
 * Aggregated events for a time window. Replaces useCalendarEvents from useInbox.js.
 * @param {Date|string} from - inclusive start
 * @param {Date|string} to - exclusive end
 * @param {{ filters?: {key: string, params: string}, enabled?: boolean }} [options]
 *   filters: from buildTagFilters — `key` joins the cache key, `params` the URL;
 *   enabled: false skips the fetch (a surface still resolving which day to show).
 */
export function useCalendarEvents(from, to, { filters = NO_FILTERS, enabled = true } = {}) {
    const { getAuthHeader } = useAuth();
    const fromIso = (from instanceof Date ? from : new Date(from)).toISOString();
    const toIso = (to instanceof Date ? to : new Date(to)).toISOString();

    return useQuery({
        // Mutations invalidate by the ['calendar', 'aggregated'] PREFIX, so the
        // trailing filter key never stops a write from refreshing the grid.
        queryKey: ['calendar', 'aggregated', fromIso, toIso, filters.key],
        queryFn: async () => {
            const filterParams = filters.params ? `&${filters.params}` : '';
            const data = await apiFetch(
                `/oauth/calendar/aggregated/events?time_min=${encodeURIComponent(fromIso)}&time_max=${encodeURIComponent(toIso)}${filterParams}`,
                getAuthHeader()
            );
            const events = data?.events || [];
            return {
                events,
                total: data?.total || 0,
                providers: data?.providers || { google: 'disconnected', outlook: 'disconnected' },
                // #681: the rail's rows, describing the range BEFORE the tag filter.
                totalUnfiltered: data?.total_unfiltered ?? events.length,
                tagFacets: data?.tag_facets || [],
                untaggedCount: data?.untagged_count ?? 0,
                tagsDegraded: Boolean(data?.tags_degraded),
            };
        },
        staleTime: 2 * 60 * 1000,
        placeholderData: keepPreviousData,  // avoid blank-then-pop flash on month/week navigation
        enabled,
    });
}

/**
 * The viewer's calendar tags for My calendars' "Show more tags" (#681): tags on
 * their OWN events (any date) plus `Calendar`-category tags nothing carries
 * yet, with ids. Fetched lazily — only when the list is opened, or when a
 * hidden tag has no events in view and its row needs a name.
 */
export function useCalendarTags({ enabled = true } = {}) {
    const { getAuthHeader } = useAuth();
    return useQuery({
        queryKey: ['calendar', 'tags'],
        queryFn: async () => {
            const data = await apiFetch('/oauth/calendar/tags', getAuthHeader());
            return data?.data?.tags || [];
        },
        staleTime: 5 * 60 * 1000,
        enabled,
    });
}

/**
 * POST /api/oauth/calendar/events — the canonical create path for the Calendar
 * editor page, pages/calendar/CalendarEditorPage (#514 Slice H.2; #680). Owns the success /
 * partial_success toasts and the aggregated-cache invalidation so no caller
 * carries its own copy of either.
 *
 * Errors are NOT toasted here: the caller reads `err.response?.data?.detail`
 * (via `mutateAsync`) so the Teams-refusal 422's actionable copy (#514 D1)
 * reaches the user verbatim, without a second generic toast on top.
 */
export function useCreateCalendarEvent() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload) => {
            return axios.post(`${API}/oauth/calendar/events`, payload, { headers: getAuthHeader() });
        },
        onSuccess: (res) => {
            queryClient.invalidateQueries({ queryKey: ['calendar', 'aggregated'] });
            if (res.data.partial_success) {
                // Copy (and the permanence decision behind it) lives in
                // lib/calendarWarnings.js — MeetingActionModal shows the same
                // decision with a meeting-centric noun (#514 audit A10).
                toast.warning(calendarWriteWarning(res.data, { variant: 'event' }));
            } else {
                // A21: every surface that opens this path is titled "Schedule
                // Meeting", so the confirmation says so too — it used to read
                // "Event created", a third noun for the same action.
                toast.success('Meeting scheduled');
            }
        },
    });
}

export function useUpdateCalendarEvent() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ eventId, source = 'google', ...payload }) => {
            // Strip provider prefix for Google/Outlook/CRM; keep book_ for coach_booking (backend strips)
            const cleanId = source === 'coach_booking' ? eventId : eventId.replace(/^(gcal_|oc_|crm_)/, '');
            return axios.put(`${API}/oauth/calendar/events/${cleanId}`, {
                ...payload,
                _source: source, // backend uses _source to route to correct provider
            }, { headers: getAuthHeader() });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendar', 'aggregated'] });
        },
    });
}

/**
 * Tag names → the {add, remove} a tag save sends (#680). `before` is the
 * event's `tags` ([{tag_id, name, color}], from the aggregated payload);
 * `afterNames` is what TagInput holds. Names compare case-insensitively, as
 * the tag vocabulary does: re-typing "vip" for an attached "VIP" is no change.
 * @returns {{ addNames: string[], remove: string[] }} remove = tag_ids
 */
export function diffEventTags(before, afterNames) {
    const fold = (name) => String(name || '').trim().toLowerCase();
    const current = (before || []).filter((t) => t && t.tag_id && t.name);
    const wanted = new Set((afterNames || []).map(fold).filter(Boolean));
    const have = new Set(current.map((t) => fold(t.name)));
    const remove = current.filter((t) => !wanted.has(fold(t.name))).map((t) => t.tag_id);
    const addNames = [];
    const seen = new Set();
    for (const name of afterNames || []) {
        const key = fold(name);
        if (!key || have.has(key) || seen.has(key)) continue;
        seen.add(key);
        addNames.push(String(name).trim());
    }
    return { addNames, remove };
}

/**
 * POST /api/oauth/calendar/tags/resolve (#682): reuse an existing tag
 * untouched, or create a missing one (category `Calendar`). The endpoint
 * names a tag `tag`; the events payload calls it `name` — mapped here.
 * @returns {Promise<Array<{tag_id: string, name: string, color: string|null, created: boolean}>>}
 */
export async function resolveCalendarTags(names, headers) {
    const res = await axios.post(`${API}/oauth/calendar/tags/resolve`, { names }, { headers });
    return (res.data?.data?.tags || []).map((t) => ({
        tag_id: t.tag_id, name: t.tag, color: t.color ?? null, created: Boolean(t.created),
    }));
}

/**
 * Save one event's tags (#680): diff, resolve the new names, then one
 * POST /api/oauth/calendar/events/tags (#682) with {add, remove}. Nothing is
 * sent when nothing changed. A tag is a CRM-side row — this never touches the
 * Google/Outlook event. Owns its invalidation, like the other calendar
 * mutations: the grid (['calendar','aggregated']) and My calendars' vocabulary
 * (['calendar','tags'], #681).
 *
 * Resolves to `{ changed, failed }`; `failed` is the endpoint's per-event
 * `[{event_id, reason}]` (not_found / provider_unavailable / series_pending).
 * Errors are not toasted here — the caller words them (lib/tagCopy.js).
 */
export function useSaveEventTags() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ eventId, before, afterNames }) => {
            const { addNames, remove } = diffEventTags(before, afterNames);
            if (!addNames.length && !remove.length) return { changed: false, failed: [] };
            const headers = getAuthHeader();
            const add = addNames.length ? (await resolveCalendarTags(addNames, headers)).map((t) => t.tag_id) : [];
            const res = await axios.post(
                `${API}/oauth/calendar/events/tags`,
                { event_ids: [eventId], add, remove },
                { headers },
            );
            return { changed: true, failed: res.data?.data?.failed || [] };
        },
        onSuccess: (result) => {
            if (!result?.changed) return;
            queryClient.invalidateQueries({ queryKey: ['calendar', 'aggregated'] });
            queryClient.invalidateQueries({ queryKey: ['calendar', 'tags'] });
        },
    });
}

export function useDeleteCalendarEvent() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ eventId, source = 'google', bookingId = null }) => {
            // A booking-link booking is cancelled as a BOOKING, whatever chip it
            // wears: the aggregator shows the Google/Outlook twin the booking
            // created (decorated with booking_id), or the bl_ chip when there is
            // no twin. Either way the backend's booking_link branch deletes the
            // provider event, marks the record cancelled and emails the guest —
            // deleting the provider event alone would leave the booking
            // confirmed and its chip would come back.
            if (bookingId) {
                return axios.delete(`${API}/oauth/calendar/events/bl_${bookingId}`, {
                    headers: getAuthHeader(),
                    params: { _source: 'booking_link' },
                });
            }
            // Pass _source as query param so backend routes correctly (DELETE has no body)
            const cleanId = source === 'coach_booking' ? eventId : eventId.replace(/^(gcal_|oc_|crm_)/, '');
            return axios.delete(`${API}/oauth/calendar/events/${cleanId}`, {
                headers: getAuthHeader(),
                params: { _source: source },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendar', 'aggregated'] });
        },
    });
}

export function useRescheduleEvent() {
    const { getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ eventId, start, end }) => {
            // Backend routes by _source. For coach_booking, it strips the book_ prefix itself.
            // For google/outlook, the event_id is the raw provider ID (gcal_/oc_ prefix already stripped by the frontend).
            const source = eventId.startsWith('gcal_') ? 'google'
                : eventId.startsWith('oc_') ? 'outlook'
                : eventId.startsWith('book_') ? 'coach_booking'
                : eventId.startsWith('crm_') ? 'crm'
                : eventId.startsWith('bl_') ? 'booking_link'
                : 'google';
            // Strip provider prefixes for Google/Outlook/CRM (their APIs expect raw IDs).
            // Keep the book_/bl_ prefix for coach_booking/booking_link — the backend strips it
            // (booking_link is refused there: a chip with no provider twin has nothing to move).
            const cleanId = (source === 'coach_booking' || source === 'booking_link')
                ? eventId : eventId.replace(/^(gcal_|oc_|crm_)/, '');
            return axios.put(`${API}/oauth/calendar/events/${cleanId}`, {
                start, end, _source: source, // backend uses _source to route to correct provider
            }, { headers: getAuthHeader() });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['calendar', 'aggregated'] });
        },
    });
}
